
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { DataTableHeader } from 'vuetify';
import { SystemUserInterface, SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import DeleteSystemUserDialog from '@/components/dialogs/system-users/DeleteSystemUserDialog.vue';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import SystemUserLogsDialog from '@/components/dialogs/system-users/SystemUserLogsDialog.vue';
import SyncQuotasDialog from '@/components/dialogs/system-users/SyncQuotasDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    DeleteSystemUserDialog,
    SystemUserLogsDialog,
    SyncQuotasDialog,
    PageHeader,
  },
  inject: [
    'systemUserRepository',
    'serverRepository',
    'websiteRepository',
  ],
})
export default class AllSystemUsers extends Vue {
  public loading: boolean = true;

  public systemUserRepository!: SystemUserRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteRepository!: WebsiteRepositoryInterface;

  public servers: ServerInterface[] = [];
  public websites: Website[] = [];
  public systemUsers: SystemUserInterface[] = [];

  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public options: ListPaginationOptions = defaultListOptions();
  public totalItems: number = 0;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public search: string = '';

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Name', value: 'name' },
    { text: 'Website', value: 'websiteId' },
    { text: 'Server', value: 'server' },
    { text: 'Directory', value: 'directory' },
    { text: 'Quota', value: 'quota.size' },
    { text: 'Used', value: 'quota.used' },
    { text: 'Expire Date', value: 'expireDate' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public systemUserStatuses: { [num: number]: string } = {
    0: 'CREATING',
    1: 'CREATED',
    2: 'ERROR',
    3: 'ACTIVE',
    4: 'DISABLED',
  };

  public deleteSystemUserDialog: string = '';
  public deleteSystemUserForDialog: SystemUserInterface | false = false;

  public systemUserLogsDialog: string = '';
  public systemUserForLogsDialog: SystemUserInterface | false = false;

  public syncQuotasDialog: string = '';
  public serversForSyncDialog: ServerInterface[] = [];

  public async created(): Promise<void> {
    try {
      this.applyRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public buildListQuery(): { [key: string]: string } {
    return buildListPaginationQuery(this.options);
  }

  public listQueryEquals(query: { [key: string]: string | (string | null)[] | null | undefined }): boolean {
    return listPaginationQueryEquals(query, this.options);
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = this.buildListQuery();
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'system_users_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async onSearch(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.initialize();
  }

  public async onPageChange(): Promise<void> {
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    try {
      await this.syncRouteQuery();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'system_users_list') {
      return;
    }
    if (this.listQueryEquals(this.$route.query)) {
      return;
    }

    this.applyRouteQuery();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;
      await this.getSystemUsers();
      await this.getServers();
      await this.getWebsites();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async getSystemUsers(): Promise<void> {
    this.systemUsers = [];
    let search = this.getSearchString();

    if (search) {
      const websiteSearch = this.websites.find((w) => w.domain === search.trim());
      if (websiteSearch) {
        search = websiteSearch.id.toString();
      }
    }

    [this.systemUsers, this.totalItems] = await this.systemUserRepository.findSystemUsers(
      this.options.page,
      search,
      effectivePageSize(this.options.itemsPerPage),
    );
  }

  public async getServers(): Promise<void> {
    const serversIds = this.systemUsers.map((systemUser) => systemUser.server);
    if (!serversIds.length) {
      return;
    }

    this.servers = await this.serverRepository.getServersByIds(serversIds);
  }

  public async getWebsites(): Promise<void> {
    const websitesIds = this.systemUsers.map((systemUser) => systemUser.websiteId);
    if (!websitesIds.length) {
      return;
    }

    this.websites = await this.websiteRepository.getWebsitesByIds(websitesIds);
  }

  public getSearchString(): string | undefined {
    if (this.search.trim() === '') {
      return undefined;
    }

    const searchServer = this.servers.find((server) => server.hostname === this.search.trim());
    if (searchServer) {
      return searchServer._id;
    }

    return this.search.trim();
  }

  public async createSystemUser(): Promise<void> {
    await this.$router.push({ name: 'system_user_create' });
  }

  public async editSystemUser(systemUserId: number): Promise<void> {
    await this.$router.push({ name: 'system_user_edit', params: { systemUserId: String(systemUserId) } });
  }

  public deleteSystemUser(systemUser: SystemUserInterface): void {
    this.deleteSystemUserDialog = 'delete-system-user-dialog';
    this.deleteSystemUserForDialog = systemUser;
  }

  public filterSystemUsers(systemUser: SystemUserInterface): void {
    this.systemUsers = this.systemUsers.filter((su) => su.id !== systemUser.id);
  }

  public getServerName(serverId: string): string {
    const server = this.servers.find((s) => s._id === serverId);
    if (!server) {
      return '';
    }

    return server.hostname;
  }

  public getWebsiteDomain(websiteId: number): string {
    const website = this.websites.find((w) => w.id === websiteId);
    if (!website) {
      return '';
    }

    return website.domain;
  }

  public showLogsDialog(systemUser: SystemUserInterface): void {
    this.systemUserForLogsDialog = systemUser;
    this.systemUserLogsDialog = 'system-user-logs-dialog';
  }

  public async deleteLog(systemUserId: number, logId: number): Promise<void> {
    try {
      await this.systemUserRepository.deleteLog(logId);
      this.$successDialog.show('Log deleted successfully');

      const index = this.systemUsers.findIndex((systemUser) => systemUser.id === systemUserId);
      if (index === -1) {
        return;
      }

      this.systemUsers[index].logs = this.systemUsers[index].logs.filter((log) => log.id !== logId);

    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message || e.message);
    }
  }

  public async showSyncQuotesDialog(): Promise<void> {
    this.serversForSyncDialog = await this.serverRepository.getAllServers();
    this.syncQuotasDialog = 'sync-quotas-dialog';
  }

  public async syncQuotas(server: string): Promise<void> {
    try {
      await this.systemUserRepository.syncQuotas(server);
    } catch (err) {
      this.$errorDialog.show(`Failed to start quotas sync for server ${server}`);
    }
  }
}
